import React, { useEffect, useState } from 'react';
import config from '../../config';
import { Modal, Button } from 'react-bootstrap';
import { FaTrash, FaBan, FaCheckCircle, FaEdit } from 'react-icons/fa';

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const { API_URL } = config;

  useEffect(() => {
    fetch(`${API_URL}/api/auth/fetchAllUsers`)
      .then((res) => res.json())
      .then((data) => {
        setUsers(data.users);
        setFilteredUsers(data.users);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        setLoading(false);
      });
  }, [API_URL]);

  const toggleRow = (id) => {
    setExpandedRows((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };
  const handleDelete = (uid) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      fetch(`${API_URL}/api/auth/deleteUser`, { method: 'POST',
        headers: {
          'Content-Type': 'application/json',
       },
       body: JSON.stringify({ uid })
      }
      )
        .then(() => {
          setUsers(users.filter(user => user._id !== uid));
          setFilteredUsers(filteredUsers.filter(user => user._id !== uid));
        })
        .catch(error => console.error("Error deleting user:", error));
    }
  };


  const handleBanToggle = async (uid, isBanned) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/updateBanUser/${uid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ban: !isBanned })  
      });
  
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to update ban status");
      }
      setUsers(users.map(user => user._id === uid ? { ...user, banned: !isBanned } : user));
      setFilteredUsers(filteredUsers.map(user => user._id === uid ? { ...user, banned: !isBanned } : user));
    } catch (error) {
      console.error("Error toggling ban:", error.message);
      alert("Failed to update ban status: " + error.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser({...user});
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditingUser(prev => ({...prev, [field]: value}));
  };

  const handleUpdateUser = async () => {
    try {
      const { _id, ...updateData } = editingUser;
      const response = await fetch(`${API_URL}/api/auth/updateUserById`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ _id, updateData }),
      });
      const data = await response.json();
      alert(data.message || 'User updated successfully');
      setShowEditModal(false);
      // Refresh users
      const res = await fetch(`${API_URL}/api/auth/fetchAllUsers`);
      const userData = await res.json();
      setUsers(userData.users);
      setFilteredUsers(userData.users);
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update user");
    }
  };
  

  const handleSearch = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchTerm(searchValue);

    const filtered = users.filter((user) =>
      user.username.toLowerCase().includes(searchValue) ||
      user.email.toLowerCase().includes(searchValue) ||
      (user.addresses[0]?.area || '').toLowerCase().includes(searchValue) ||
      user.contact.toLowerCase().includes(searchValue)
    );

    setFilteredUsers(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[98vh]">
        <div className="text-center text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-4">USERS</h1>

      {/* Search Bar */}
      <input
        type="text"
        value={searchTerm}
        onChange={handleSearch}
        placeholder="Search by username, email, city, or contact..."
        className="mb-4 p-2 border border-gray-300 w-full rounded-xl"
      />

      <table className="min-w-full bg-white">
        <thead>
          <tr className="bg-gray-100 rounded-t-lg">
            <th className="py-2 px-4  border-black border-b text-left font-semibold rounded-tl-lg">Username</th>
            <th className="py-2 px-4 border-black border-b text-left font-semibold">Email</th>
            <th className="py-2 px-4 border-black border-b text-left font-semibold">City</th>
            <th className="py-2 px-4 border-black border-b text-left font-semibold">Contact</th>
            <th className="py-2 px-4 border-black border-b text-left font-semibold rounded-tr-lg">More Details</th>
            <th className="py-2 px-4 border-black border-b text-left font-semibold rounded-tr-lg">Action</th>
          </tr>
        </thead>

        <tbody>
          {filteredUsers.map((user) => (
            <React.Fragment key={user._id}>
              <tr className="hover:bg-gray-50">
                <td className="py-2 px-4 border-b">{user.username}</td>
                <td className="py-2 px-4 border-b">{user.email}</td>
                <td className="py-2 px-4 border-b">{user.addresses[0]?.area || 'N/A'}</td>
                <td className="py-2 px-4 border-b">{user.contact}</td>
              
                <td className="py-2 px-4 border-b text-center">
                  <button
                    onClick={() => toggleRow(user._id)}
                    className="text-blue-500 hover:underline focus:outline-none"
                  >
                    {expandedRows[user._id] ? '▲' : '▼'}
                  </button>
                </td>
                <td className="py-2 px-4 border-b text-center">
                     <div className="flex items-center justify-center space-x-4">
    
                       <div className="relative group">
                         <button onClick={() => handleEdit(user)} className="text-blue-500 hover:text-blue-700">
                        <FaEdit />
                       </button>
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-all bg-gray-800 text-white text-xs px-2 py-1 rounded">
                      Edit
                      </span>
                     </div>

                       <div className="relative group">
                         <button onClick={() => handleDelete(user._id)} className="text-red-500 hover:text-red-700">
                        <FaTrash />
                       </button>
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-all bg-gray-800 text-white text-xs px-2 py-1 rounded">
                      Delete
                      </span>
                     </div>

                    <div className="relative group">
                       <button onClick={() => handleBanToggle(user._id, user.isBanned)}
                              className={`${user.banned ? 'text-green-500 hover:text-green-700' : 'text-yellow-500 hover:text-yellow-700'}`}
                        >
                          {user.isBanned ? <FaCheckCircle /> : <FaBan />}
                       </button>
                       <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-all bg-gray-800 text-white text-xs px-2 py-1 rounded">
                          {user.banned ? 'Unban' : 'Ban'}
                      </span>
                    </div>
                   </div>
                </td>
              </tr>
              {expandedRows[user._id] && (
                <tr className="bg-white">
                  <td colSpan="5" className="p-4 border-b">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-semibold text-[22px]">Additional Details</h4>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleEdit(user)}
                      >
                        <FaEdit className="me-1" /> Edit Details
                      </button>
                    </div>
                    <div className="flex justify-between">
                      <div className="space-y-2">
                        <div className='flex items-center gap-3'>
                          {user.userImage && (
                            <img
                              src={user.userImage}
                              alt={`${user.username}'s profile`}
                              className="w-16 h-16 rounded-full mt-2"
                            />
                          )}
                          <p className='font-semibold text-[18px] text-[#23272E] leading-[22px]'>{user.username}</p>
                        </div>
                      </div>
                      <div className='flex gap-3'>
                        <div className='w-[1px] h-[90%] bg-[#DBDEDA]'></div>
                        <div>
                          <p className='font-medium text-[13px] leading-[15.28px] text-[#8B909A] mb-2'>USER INFORMATION</p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>City: <span className='font-semibold'>{user.addresses[0].area || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Address: <span className='font-semibold'>{user.addresses[0].address || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Hint: <span className='font-semibold'>{user.addresses[0].hint || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Contact: <span className='font-semibold'>{user.contact || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Gender: <span className='font-semibold'>{user.gender || 'N/A'}</span></p>
                        </div>
                      </div>
                      <div className='flex gap-3'>
                        <div className='w-[1px] h-[90%] bg-[#DBDEDA]'></div>
                        <div>
                          <p className='font-medium text-[13px] leading-[15.28px] text-[#8B909A] mb-2'>OTHER INFORMATION</p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Referred By: <span className='font-semibold'>{user.referredby?.reference || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Referral Code: <span className='font-semibold'>{user.referredby?.referredby || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Latitude: <span className='font-semibold'>{user.addresses[0].latitude || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>Longitude: <span className='font-semibold'>{user.addresses[0].longitude || 'N/A'}</span></p>
                          <p className='font-normal text-[13px] text-[#23272E] leading-[22px]'>SaveAs: <span className='font-semibold'>{user.addresses[0].saveAs || 'N/A'}</span></p>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingUser && (
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Username</label>
                <input type="text" className="form-control" value={editingUser.username || ''}
                  onChange={(e) => handleEditChange('username', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={editingUser.email || ''}
                  onChange={(e) => handleEditChange('email', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Contact</label>
                <input type="text" className="form-control" value={editingUser.contact || ''}
                  onChange={(e) => handleEditChange('contact', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Gender</label>
                <select className="form-control" value={editingUser.gender || ''}
                  onChange={(e) => handleEditChange('gender', e.target.value)}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleUpdateUser}>Save Changes</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserTable;
